import { useEffect, useMemo, useRef } from 'react'

import { useUpdateMultipleOrganizationSettings } from '@modules-deprecated/app/organization/hooks/useUpdateMultipleOrganizationSettings'
import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'
import { useUserOrganizationSettings } from '@modules-deprecated/app/organization/userOrganizationSettingsContext'
import { useUmbrellaStatus } from '@modules-deprecated/app/umbrellas/hooks/useUmbrellaStatus'

import { useOrganizationSource } from '../../../hooks/useOrganizationSource'
import { useSplitTest } from '../../../hooks/useSplitTest/useSplitTest'
import { onboardingExtendedQuestionnaireBuckets } from '../constants/onboardingExtendedQuestionnaireBuckets'
import { onboardingFirstStep } from '../constants/onboardingFirstStep'
import { OnboardingPlatform } from '../enums/onboardingPlatform'
import { OnboardingVariant } from '../enums/onboardingVariant'
import { getOnboardingVariantPayload } from '../utils/getOnboardingVariantPayload'
import { isEligibleForOnboarding } from '../utils/isEligibleForOnboarding'

export const useOnboardingVariant = () => {
  const hasProcessedRef = useRef(false)
  const { organization } = useUserOrganization()
  const { isFetched, organizationSettings } = useUserOrganizationSettings()
  const { update } = useUpdateMultipleOrganizationSettings()
  const { hasUmbrellas: isUmbrellaMember } = useUmbrellaStatus()
  const { isSourceBilly } = useOrganizationSource()
  const { isEnabled: isExtendedQuestionnaire } = useSplitTest({ buckets: onboardingExtendedQuestionnaireBuckets })

  const organizationId = organization?.id
  const onboardingVariant = organizationSettings?.onboardingVariant
  const onboardingPlatform = organizationSettings?.onboardingPlatform

  // New orgs (no saved variant) always get Rollout.
  const assignedOnboardingVariant =
    onboardingVariant ?? (organization?.randomBucket !== undefined ? OnboardingVariant.Rollout : undefined)

  const firstStep = useMemo(() => {
    const isWebPlatform = !onboardingPlatform || onboardingPlatform === OnboardingPlatform.Web

    if (!isWebPlatform || !assignedOnboardingVariant || assignedOnboardingVariant === OnboardingVariant.Control) {
      return undefined
    }

    if (!isEligibleForOnboarding({ isSourceBilly, isUmbrellaMember, organization })) {
      return undefined
    }

    return onboardingFirstStep
  }, [assignedOnboardingVariant, isSourceBilly, isUmbrellaMember, onboardingPlatform, organization])

  useEffect(() => {
    const isReadyToProcess = isFetched && !!organizationId && !!assignedOnboardingVariant && !hasProcessedRef.current

    if (isReadyToProcess) {
      hasProcessedRef.current = true

      const isWebPlatform = !onboardingPlatform || onboardingPlatform === OnboardingPlatform.Web
      if (isWebPlatform && assignedOnboardingVariant !== OnboardingVariant.Control && !onboardingVariant) {
        update({ organizationId, payload: getOnboardingVariantPayload(assignedOnboardingVariant) })
      }
    }
  }, [assignedOnboardingVariant, isFetched, onboardingPlatform, onboardingVariant, organizationId, update])

  return { firstStep, isExtendedQuestionnaire, variant: assignedOnboardingVariant }
}
